/**
 * Owner: Masa Bayu
 * File: library-state-machine.ts
 * Date:Time: 2026-09-16 06:40
 * Description: T4-E2 library item state transitions + interrupted recovery.
 */
import { assertT4E1SecurityIntact } from './library-security';
/** User-facing shelf states emphasized in T4-E2 (subset of full item states). */
export const QXREAD_LIBRARY_SHELF_STATES = [
    'owned',
    'available',
    'blocked',
    'reading',
    'completed',
    'archived',
];
const ALLOWED_TRANSITIONS = Object.freeze({
    materializing: Object.freeze(['available', 'blocked', 'removed']),
    available: Object.freeze([
        'reading',
        'completed',
        'update_available',
        'offline_preparing',
        'blocked',
        'removed',
        'archived',
    ]),
    reading: Object.freeze([
        'available',
        'completed',
        'update_available',
        'offline_preparing',
        'offline_ready',
        'blocked',
        'removed',
        'archived',
    ]),
    completed: Object.freeze([
        'reading',
        'available',
        'update_available',
        'blocked',
        'removed',
        'archived',
    ]),
    update_available: Object.freeze([
        'available',
        'reading',
        'completed',
        'offline_preparing',
        'blocked',
        'removed',
        'archived',
    ]),
    offline_preparing: Object.freeze([
        'offline_ready',
        'offline_stale',
        'available',
        'reading',
        'blocked',
    ]),
    offline_ready: Object.freeze([
        'offline_stale',
        'reading',
        'available',
        'completed',
        'blocked',
    ]),
    offline_stale: Object.freeze(['offline_preparing', 'available', 'reading', 'blocked']),
    blocked: Object.freeze(['available', 'removed']),
    removed: Object.freeze([]),
    archived: Object.freeze(['available', 'reading', 'completed', 'removed']),
});
export function canTransitionLibraryItemState(from, to) {
    if (from === to) {
        return true;
    }
    return ALLOWED_TRANSITIONS[from].includes(to);
}
export function transitionLibraryItemState(item, to) {
    assertT4E1SecurityIntact();
    if (!canTransitionLibraryItemState(item.state, to)) {
        throw new Error(`QXRead library cannot transition "${item.state}" → "${to}"`);
    }
    return Object.freeze({
        ...item,
        state: to,
        offlineAvailable: to === 'offline_ready'
            ? true
            : to === 'blocked' || to === 'removed' || to === 'archived'
                ? false
                : item.offlineAvailable,
        updatedAt: new Date().toISOString(),
    });
}
export function archiveLibraryItem(item) {
    return transitionLibraryItemState(item, 'archived');
}
export function unarchiveLibraryItem(item) {
    if (item.state !== 'archived') {
        throw new Error('QXRead unarchive requires an archived library item');
    }
    const restored = item.progress.state === 'completed'
        ? 'completed'
        : item.progress.state === 'in_progress'
            ? 'reading'
            : 'available';
    return transitionLibraryItemState(item, restored);
}
/** Map internal item state to E2 shelf vocabulary. */
export function toLibraryShelfState(item) {
    if (item.state === 'blocked') {
        return 'blocked';
    }
    if (item.state === 'archived') {
        return 'archived';
    }
    if (item.state === 'completed' || item.progress.state === 'completed') {
        return 'completed';
    }
    if (item.state === 'reading' || item.progress.state === 'in_progress') {
        return 'reading';
    }
    if (item.state === 'available' ||
        item.state === 'update_available' ||
        item.state === 'offline_ready') {
        return 'available';
    }
    if (item.state === 'materializing') {
        return 'owned';
    }
    return 'owned';
}
/**
 * Recover interrupted reading: if progress says in_progress but shelf drifted to available.
 */
export function recoverInterruptedReadingState(item) {
    assertT4E1SecurityIntact();
    if (item.state === 'blocked' || item.state === 'removed' || item.state === 'archived') {
        return item;
    }
    if (item.progress.state === 'in_progress' && item.state === 'available') {
        return transitionLibraryItemState(item, 'reading');
    }
    if (item.progress.state === 'completed' &&
        item.state !== 'completed' &&
        item.state !== 'update_available') {
        return transitionLibraryItemState(item, 'completed');
    }
    return item;
}
//# sourceMappingURL=library-state-machine.js.map